import { useState, useCallback } from 'react';
import { generateAIResponse } from '../../../../services/AIServices';

const PROMPT_ANALISE_CONVERSA_DEFAULT = `Atue como um engenheiro de prompt.
Receberá:

O prompt do agente

O histórico recente da conversa

Sua tarefa é gerar um relatório curto e direto contendo:

Possíveis falhas no prompt

Em qual parte do prompt cada falha ocorre

Onde o agente errou com base no histórico

Sugestões simples de melhoria

O relatório deve ser claro, breve e fácil de entender, sem aprofundar demais.`;

const PROMPT_DEBUG_MENSAGEM_DEFAULT = `Analise esta interação específica e explique:

1. **Por que o agente deu esta resposta?**
   - Possível interpretação do contexto
   - Lógica por trás da resposta

2. **A resposta foi apropriada?**
   - Se sim, por quê?
   - Se não, o que estava errado?

3. **Como melhorar?**
   - Sugestões específicas de prompt
   - Instruções mais claras

CONTEXTO:
Usuário disse: "{userMessage}"
Agente respondeu: "{agentResponse}"

Seja específico e prático nas recomendações.`;

export const useDebugAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const analyzeConversation = useCallback(async (conversationHistory, customPrompt = null) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Analisando conversa completa...', { conversationHistoryLength: conversationHistory.length });
      
      const response = await generateAIResponse({
        prompt: "",
        debug_prompt: true,
        conversation_history: conversationHistory,
        system_message: "",
        model: "gpt-4.1-mini",
        max_tokens: 1500,
        temperature: 0.3,
      });

      if (!response.success) {
        throw new Error(response.error || 'Erro ao analisar conversa');
      }

      return {
        success: true,
        analysis: response.response,
        tokensUsed: response.tokens_used,
        timestamp: new Date().toISOString()
      };
    } catch (err) {
      console.error('❌ Erro na análise da conversa:', err);
      setError(err.message || 'Erro inesperado na análise');
      return {
        success: false,
        error: err.message || 'Erro inesperado na análise'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const analyzeMessage = useCallback(async (userMessage, agentResponse, customPrompt = null) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Analisando mensagem específica...', { userMessage, agentResponse });
      
      // Formatando as mensagens para debug específico
      const messageContext = {
        userMessage: userMessage || 'Início da conversa',
        agentResponse: agentResponse
      };
      
      const response = await generateAIResponse({
        prompt: customPrompt || PROMPT_DEBUG_MENSAGEM_DEFAULT,
        debug_prompt: true,
        debug_message: true,
        conversation_history: messageContext,
        system_message: "",
        model: "gpt-4.1-mini",
        max_tokens: 1000,
        temperature: 0.3
      });

      if (!response.success) {
        throw new Error(response.error || 'Erro ao analisar mensagem');
      }

      return {
        success: true,
        analysis: response.response,
        tokensUsed: response.tokens_used,
        timestamp: new Date().toISOString()
      };
    } catch (err) {
      console.error('❌ Erro na análise da mensagem:', err);
      setError(err.message || 'Erro inesperado na análise');
      return {
        success: false,
        error: err.message || 'Erro inesperado na análise'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    resetError,
    analyzeConversation,
    analyzeMessage,
    defaultPrompts: {
      conversation: PROMPT_ANALISE_CONVERSA_DEFAULT,
      message: PROMPT_DEBUG_MENSAGEM_DEFAULT
    }
  };
};

export default useDebugAnalysis;